import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUpdateTeamMember, type TeamMember } from '@/hooks/useTeamManagement';
import { useRoleDefinitions } from '@/hooks/usePermissions';
import { User, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  member: TeamMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditUserDialog({ member, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(member.full_name);
  const [roleSlug, setRoleSlug] = useState(member.role_definition_slug || member.role);
  const [department, setDepartment] = useState(member.department || '');

  const update = useUpdateTeamMember();
  const { data: roles } = useRoleDefinitions();

  useEffect(() => {
    setFullName(member.full_name);
    setRoleSlug(member.role_definition_slug || member.role);
    setDepartment(member.department || '');
  }, [member]);

  const availableRoles = roles?.filter(r => r.slug !== 'org_owner') || [];

  const handleSubmit = async () => {
    await update.mutateAsync({
      target_user_id: member.user_id,
      full_name: fullName !== member.full_name ? fullName : undefined,
      role_slug: roleSlug !== (member.role_definition_slug || member.role) ? roleSlug : undefined,
      department: department !== (member.department || '') ? department : undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{t('hire.team.editUser.title')}</DialogTitle>
          <DialogDescription>
            {t('hire.team.editUser.description', { name: member.full_name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.account.fullName')}</label>
            <div className="relative">
              <User className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="ps-9 h-10 rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.settings.loginEmail')}</label>
            <Input value={member.email} disabled className="h-10 rounded-xl bg-muted/50 cursor-not-allowed" />
            <p className="text-xs text-muted-foreground">{t('hire.team.editUser.emailLockedHint')}</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.team.usersList.columns.role')}</label>
            <Select value={roleSlug} onValueChange={setRoleSlug}>
              <SelectTrigger className="h-10 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r.slug} value={r.slug}>{t(`hire.roles.slugLabels.${r.slug}`, { defaultValue: r.name })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.team.usersList.columns.department')}</label>
            <div className="relative">
              <Building2 className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder={t('hire.team.inviteUser.departmentPlaceholder')}
                className="ps-9 h-10 rounded-xl"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl"
            onClick={handleSubmit}
            disabled={update.isPending}
          >
            {update.isPending ? t('hire.account.saving') : t('hire.account.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
